import axios from 'axios'
import exceptionHandler from '@/common/exception-handler'

const getLocations = companyId => {
  return axios.get(`/api/tenants/${companyId}`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const getGroups = companyId => {
  return axios.get(`/api/tenants/${companyId}/groups`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const getAllTenants = companyId => {
  return axios.get(`/api/tenants/${companyId}/any`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const getLocation = (companyId, locationId) => {
  return axios.get(`/api/tenants/${companyId}/${locationId}`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

// TPOS-10799: the location PUT copies every licensing flag from the request body, and a listing entry
// (getLocations, getAllTenants, ...) lacks some of them, such as smsMessagingManualOnly. Never save a
// listing entry back: read the location's details and apply only the fields being changed.
const detailsWithChanges = (companyId, locationId, changes) => {
  return getLocation(companyId, locationId)
    .then(details => ({ ...details, ...changes }))
}

const getGroupTenants = (companyId, groupId) => {
  return axios.get(`/api/tenants/${companyId}/group/${groupId}`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const createLocation = (companyId, location) => {
  return axios.post(`/api/tenants/${companyId}`, location)
    .then((res) => {
      localStorage.removeItem('companyLevel')
      return res.data
    })
    .catch(error => exceptionHandler.parse(error))
}

const editLocation = (companyId, location) => {
  return axios.put(`/api/tenants/${companyId}`, location)
    .then((res) => {
      localStorage.removeItem('companyLevel')
      return res.data
    })
    .catch(error => {
      return exceptionHandler.parse(error)
    })
}

const saveLocation = (companyId, location) => {
  return axios.put(`/api/tenants/${companyId}`, location)
    .then((res) => {
      return res.data
    })
    .catch(error => exceptionHandler.parse(error))
}

const validateDeliverectCredentials = (accountId, locationId) => {
  return axios.get(`/api/tenants/validate-deliverect/${accountId}/${locationId}`)
    .then(res => {
      return res.data
    })
    .catch(error => exceptionHandler.parse(error))
}

const getTenantTags = (companyId) => {
  return axios.get(`/api/tenants/tags/${companyId}`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const deleteLocation = (companyId, locationId) => {
  return axios.post(`/api/tenants/${companyId}/${locationId}/delete`)
    .then(() => { localStorage.removeItem('companyLevel') })
    .catch(error => exceptionHandler.parse(error))
}

const getCredentials = (companyId, locationId) => {
  return axios.get(`/api/tenants/${companyId}/${locationId}/api-credentials`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const deleteCredential = (companyId, locationId, pubCred) => {
  return axios.post(`/api/tenants/${companyId}/${locationId}/api-credentials/${pubCred}/delete`)
    .catch(error => exceptionHandler.parse(error))
}

const getLicenseStatus = (locationId) => {
  return axios.get(`/api/licensing/status/${locationId}`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const revokeLicense = (locationId) => {
  return axios.post(`/api/licensing/revoke/${locationId}`)
    .then(ignored => true)
    .catch(error => exceptionHandler.parse(error))
}

const getLicenseToken = (locationId) => {
  return axios.get(`/api/licensing/get-token/${locationId}`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const awaitTokenDeath = (locationId) => {
  return axios.get(`/api/licensing/await-token-death/${locationId}`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const getBrands = () => {
  return axios.get('/api/companies/brands')
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

export default {
  getLocations,
  getGroups,
  getAllTenants,
  getGroupTenants,
  getLocation,
  detailsWithChanges,
  getTenantTags,
  createLocation,
  editLocation, // saves location, but removes company level from localstorage.
  saveLocation, // just saves the location and returns the response.
  deleteLocation,
  getCredentials,
  deleteCredential,
  getLicenseStatus,
  revokeLicense,
  getLicenseToken,
  awaitTokenDeath,
  getBrands,
  validateDeliverectCredentials
}
